/**
 * We want to extend our monaco editor so that whenever there is a line with a class
 *
 * e.g.
 * class Person {
 *  ...
 *
 *  Above that class we want to render commands
 *  e.g.
 *  Open Entities | Synchronize Entities <-- this is rendered via a code lens
 *  class Person {
 *  ...
 *
 */
import { findTokens, vklTokenTypes as types, vklTypes } from 'studio/pages/Editor/VklSyntaxHighlighter';
import { syncPolicies } from 'studio/pages/Synchronization/Synchronization';

export class EntitiesCodeLensProvider {
  constructor(monaco, activeFileSynchronizableConcepts, openEntitiesCommand, synchronizeCommand) {
    this.monaco = monaco;
    this.activeFileSynchronizableConcepts = activeFileSynchronizableConcepts;
    this.openEntities = openEntitiesCommand;
    this.synchronize = synchronizeCommand;
  }

  async provideCodeLenses(model, token) {
    const codeLenses = [];
    model.getLinesContent().forEach((line, lineNumberZeroIndexed) => {
      const tokens = findTokens(
        this.monaco,
        model,
        new this.monaco.Position(lineNumberZeroIndexed + 1, 1),
        [types.keyword, types.identifier, types.curlyBracket],
        [],
        true
      );
      // check that the keyword token is a 'class' and that is on the current line
      if (tokens?.[0].value === vklTypes.class && tokens[0].position.lineNumber === lineNumberZeroIndexed + 1) {
        const position = tokens[0].position;
        const className = tokens?.[1].value;
        const vklClass = this.activeFileSynchronizableConcepts.find(c => c.data.vklIdentifier === className);
        // if we have found a class with the same name as the one in the editor, figure out which actions to render
        if (vklClass) {
          const range = new this.monaco.Range(position.lineNumber, 1, position.lineNumber, 1);
          // if the class has an entities file, render the open entities command
          if (vklClass.data.entitiesFile) {
            codeLenses.push({
              range,
              command: {
                id: this.openEntities,
                title: 'Open Entities',
                arguments: [vklClass.data.entitiesFile.path]
              }
            });
          }
          // if the class is synchronizable and the sync policy is not ignore, render the synchronize command
          if (vklClass.data.isSynchronizable && vklClass.data.syncPolicy !== syncPolicies.ignore) {
            codeLenses.push({
              range,
              command: {
                id: this.synchronize,
                title: 'Synchronize Entities',
                arguments: [vklClass.data.vklIdentifier]
              }
            });
          }
        }
      }
    });

    return {
      lenses: codeLenses,
      dispose: () => {}
    };
  }

  resolveCodeLens(model, codeLens, token) {
    return codeLens;
  }
}
